// 추가수정 2차(26-07-27) §2 — 한국시간(KST) 표시 포맷터 **정본**.
//
// 배경: +9h 시프트로 KST 를 구하는 코드가 리포에 이미 3벌 흩어져 있다
//   · packages/shared/src/signature.ts   — 문서 표시명 YYMMDD
//   · apps/api/.../signature-pdf.service.ts — formatDateTimeKst ('YYYY-MM-DD HH:mm (KST)')
//   · apps/api/.../service-orders/sla/calendar.ts — krDateKey ('YYYY-MM-DD')
// 콘솔 표시층을 KST 로 통일하면서 **4벌째를 apps/windows 로컬에 만들지 않도록**
// 표시용 정본을 여기 한 곳에 둔다. 콘솔은 apps/windows/src/lib/format-date.ts
// 의 re-export 로 가져다 쓴다.
//
// 구현 방식: Intl.DateTimeFormat 이 아니라 기존 3벌과 동일한
// **+9h 시프트 후 UTC 성분 읽기**. 한국은 서머타임(DST)이 없고 1988년 이후
// 오프셋이 UTC+9 로 고정이라 시프트만으로 정확하며, 러너 로캘·ICU 빌드에
// 흔들리지 않고(테스트가 CI/로컬에서 같은 결과) 기존 3벌과 표기가 어긋날
// 여지도 없앤다.
//
// ⚠ 캘린더 셀 키에는 쓰지 않는다 — 셀 키는 브라우저 로컬 벽시계 기준
//   toLocalYmd(apps/windows/src/lib/format-date.ts)가 정답이다(round10 B2).
//   여기 함수들은 **표시(라벨) 전용**이다.
/** KST = UTC+9 고정(대한민국은 서머타임 없음). */
export const KST_OFFSET_MS = 9 * 3600_000;
/**
 * 입력을 KST 벽시계 성분으로. null/빈값/파싱불가 → null.
 *
 * 시프트한 Date 의 **UTC 성분**을 읽는 것이 핵심 — 이러면 실행 환경의
 * 타임존과 무관하게 항상 서울 벽시계가 나온다.
 */
function kstParts(v) {
    if (v == null)
        return null;
    if (typeof v === 'string' && v.trim() === '')
        return null;
    const ms = v instanceof Date ? v.getTime() : new Date(v).getTime();
    if (Number.isNaN(ms))
        return null;
    const k = new Date(ms + KST_OFFSET_MS);
    return {
        y: String(k.getUTCFullYear()).padStart(4, '0'),
        m: String(k.getUTCMonth() + 1).padStart(2, '0'),
        d: String(k.getUTCDate()).padStart(2, '0'),
        hh: String(k.getUTCHours()).padStart(2, '0'),
        mm: String(k.getUTCMinutes()).padStart(2, '0'),
    };
}
/**
 * KST 기준 날짜 표기 — `"2026.07.27"`.
 *
 * 목록 화면의 표준 표기(결정 ②A: 목록은 날짜만). 값이 없거나 파싱 불가하면
 * **null** 을 돌려주므로 호출부가 화면에 맞는 폴백을 고른다:
 *   `kstYmd(doc.createdAt) ?? '–'`
 */
export function kstYmd(v) {
    const p = kstParts(v);
    return p ? `${p.y}.${p.m}.${p.d}` : null;
}
/**
 * KST 기준 날짜+시각 표기 — `"2026.07.27 14:30"`,
 * `label: true` 면 `"2026.07.27 14:30 (한국 시간)"`.
 *
 * 상세 화면의 표준 표기. 값이 없거나 파싱 불가하면 null(폴백은 호출부).
 * PDF 인증 페이지의 formatDateTimeKst 와 **같은 구성**(날짜 + 공백 + HH:mm +
 * 기준 라벨)이라 화면과 산출물이 같은 시각을 같은 순서로 읽힌다.
 */
export function kstDateTime(v, opts) {
    const p = kstParts(v);
    if (!p)
        return null;
    const base = `${p.y}.${p.m}.${p.d} ${p.hh}:${p.mm}`;
    return opts?.label ? `${base} (한국 시간)` : base;
}
/**
 * KST 기준 날짜를 **하이픈 표기** `"2026-07-27"` 로. 값이 없거나 파싱 불가면 null.
 *
 * 점 표기(`kstYmd`)와 같은 날짜의 다른 표기다 — `<input type="date">` 값,
 * 파일명, 기계가 다시 읽을 수 있는 문구처럼 **하이픈이 규약인 자리** 전용.
 * 어느 쪽을 쓸지는 "화면 표기냐(점) / 값·규약이냐(하이픈)"로 가른다.
 *
 * 한국시간 통일 4차(26-08-14) — 이 함수를 만든 이유: 하이픈 표기가 필요한
 * 호출부가 각자 `kstYmd(d)?.split('.').join('-')` 로 변환하고 있었다. 새 호출부가
 * 그 사본을 하나 더 만드는 대신 여기서 가져가게 한다(기존 PDF/QR 어댑터들은
 * 출력 문자열 불변 시험이 걸려 있어 이 차수에서 건드리지 않는다).
 */
export function kstYmdHyphen(v) {
    const p = kstParts(v);
    return p ? `${p.y}-${p.m}-${p.d}` : null;
}
/**
 * 오늘(KST) 을 `"yyyy-mm-dd"` 로 — `<input type="date">` 기본값용.
 *
 * 기존 `new Date().toISOString().slice(0, 10)` 은 UTC 기준이라 **한국 오전
 * 9시 전에는 어제 날짜**가 기본값으로 들어갔다. 그 자리를 그대로 대체할 수
 * 있도록 반환 형식은 date input 이 요구하는 하이픈 표기를 유지한다
 * (표시용 kstYmd 의 점 표기와 다름 — 의도된 차이).
 *
 * @param now 테스트에서 시각을 고정하기 위한 주입점. 생략 시 현재 시각.
 */
export function kstTodayYmd(now = Date.now()) {
    // 현재 시각은 항상 유효하므로 null 이 나올 수 없다(방어적 폴백만 유지).
    return kstYmdHyphen(now) ?? '';
}
/**
 * KST 달력 **하루**의 라벨과 그 하루에 해당하는 UTC 구간.
 *
 * 왜 라벨과 구간을 한 함수가 함께 만드는가 — 하루 단위 채번(`DEAL-YYYYMMDDNN`
 * · `SO-YYYYMMDD-PUB###`)은 **찍는 날짜**와 **세는 하루**가 반드시 같아야 한다.
 * 둘을 따로 계산하면 한쪽만 KST 로 고쳐졌을 때 `DEAL-20260814…` 가 8/13 분량을
 * 세는 상태가 되고, 그건 시험이 잡기 어려운 조용한 어긋남이다.
 *
 * @param now 테스트에서 시각을 고정하기 위한 주입점. 생략 시 현재 시각.
 */
export function kstDayRangeUtc(now = Date.now()) {
    const p = kstParts(now);
    // 현재 시각은 항상 유효하므로 null 이 나올 수 없다(방어적 폴백만 유지).
    if (!p)
        throw new RangeError('kstDayRangeUtc: invalid now');
    // KST 자정 = 그 날짜의 UTC 자정에서 9시간 뺀 순간.
    const startMs = Date.UTC(Number(p.y), Number(p.m) - 1, Number(p.d)) - KST_OFFSET_MS;
    return {
        label: `${p.y}-${p.m}-${p.d}`,
        startUtc: new Date(startMs),
        endUtc: new Date(startMs + 24 * 3600_000),
    };
}
/**
 * KST 달력 **한 해**의 라벨과 그 해에 해당하는 UTC 구간.
 *
 * 연 단위 채번(`CNT-YYYY-NNNN`)용. 한국 1월 1일 오전 0~9시는 UTC 로 아직 전년
 * 12월 31일이라, 서버 시계로 연도를 읽으면 새해 첫 계약이 **작년 번호**를 받는다.
 *
 * @param now 테스트에서 시각을 고정하기 위한 주입점. 생략 시 현재 시각.
 */
export function kstYearRangeUtc(now = Date.now()) {
    const p = kstParts(now);
    if (!p)
        throw new RangeError('kstYearRangeUtc: invalid now');
    const year = Number(p.y);
    return {
        label: p.y,
        year,
        startUtc: new Date(Date.UTC(year, 0, 1) - KST_OFFSET_MS),
        endUtc: new Date(Date.UTC(year + 1, 0, 1) - KST_OFFSET_MS),
    };
}
/**
 * KST 달력 **한 달**의 라벨과 그 달에 해당하는 UTC 구간.
 *
 * 왜 필요한가 — '이번 달' 타일이 `new Date(now.getFullYear(), now.getMonth(), 1)`
 * 로 창을 잡고 있었다. 운영 API 는 ECS 에 `TZ` 가 없어 **UTC** 로 돌기 때문에
 * 그 창의 시작은 UTC 월초(= 한국 그 달 1일 오전 9시)다. 한국 8월 1일 0~9시에
 * 찍힌 건은 8월 타일 밖으로 떨어지고, 9월이 되면 창이 통째로 옮겨가 **영원히
 * 세지 않는다**. 개발 노트북은 `Asia/Seoul` 이라 이 병이 재현되지 않는다.
 *
 * `kstDayRangeUtc` · `kstYearRangeUtc` 와 **같은 모양**이다 — 라벨과 구간을 한
 * 함수가 함께 내므로 "타일에 적힌 달"과 "세는 구간"이 갈릴 수 없다.
 *
 * ⚠ `label` 은 하이픈 표기 `"2026-08"`(= `kstDayRangeUtc` 라벨의 앞 7자리)이다.
 *   식별자 채번용 `kstYearMonth`(`"202608"`, `PP-YYYYMM-…`)와는 **다른 형식**이고
 *   서로 대체할 수 없다.
 *
 * @param now 테스트에서 시각을 고정하기 위한 주입점. 생략 시 현재 시각.
 */
export function kstMonthRangeUtc(now = Date.now()) {
    const p = kstParts(now);
    // 현재 시각은 항상 유효하므로 null 이 나올 수 없다(방어적 폴백만 유지).
    if (!p)
        throw new RangeError('kstMonthRangeUtc: invalid now');
    const year = Number(p.y);
    /** 1 = 1월 — 라벨(`p.m`)과 같은 규약. Date 의 0-based month 와 헷갈리지 않게. */
    const month = Number(p.m);
    // KST 자정 = 그 날짜의 UTC 자정에서 9시간 뺀 순간(kstDayRangeUtc 와 같은 계산).
    return {
        label: `${p.y}-${p.m}`,
        year,
        month,
        startUtc: new Date(Date.UTC(year, month - 1, 1) - KST_OFFSET_MS),
        endUtc: new Date(Date.UTC(year, month, 1) - KST_OFFSET_MS),
    };
}
/**
 * **화면이 보여 주는 한국 날짜 구간**(시작일·종료일, 양끝 포함)을 그 구간에
 * 해당하는 UTC 순간 구간으로 바꾼다. 조회는 `gte: startUtc, lt: endUtc`.
 *
 * ── 왜 필요한가 (2026-08-31, 캘린더 일정 누락 조사) ───────────────────────
 * 캘린더는 칸을 **한국 날짜**로 그리는데, 서버 조회는 클라이언트가 보낸
 * `"YYYY-MM-DD"` 를 `new Date(...)` 로 받아 **UTC 자정**으로 비교했다. 두 창이
 * 9시간 어긋나 있어서:
 *   · 격자 **첫날** — 한국 자정~오전 9시에 해당하는 값(= 전날 15:00Z. 2026-07-27
 *     이전 저장 관례가 정확히 이 모양)이 `gte` 에 미달해 그 칸이 빈다.
 *   · 격자 **끝날** — 오전 9시 이후 값이 전부 `lte`(그날 00:00Z)를 넘어 잘린다.
 *     필드 설치 캘린더는 이 때문에 **말일이 어느 달에서도 안 보였다**(8/31 사고).
 *
 * 그래서 "보이는 날짜 구간"을 받아 "그 구간의 진짜 시각 구간"을 한 곳에서
 * 만든다. `kstDayRangeUtc` · `kstMonthRangeUtc` 와 **같은 모양**(endUtc 제외)이다.
 *
 * @param startYmd 시작일 `"YYYY-MM-DD"`(포함). 하이픈·점 표기 모두 받는다.
 * @param endYmd   종료일 `"YYYY-MM-DD"`(**포함** — 그날 23:59:59.999 까지).
 * @returns 파싱 불가하면 null — 호출부가 "날짜 조건 없음"으로 처리하도록.
 */
export function kstYmdRangeUtc(startYmd, endYmd) {
    const s = kstParts(startYmd);
    const e = kstParts(endYmd);
    if (!s || !e)
        return null;
    const startMs = Date.UTC(Number(s.y), Number(s.m) - 1, Number(s.d)) - KST_OFFSET_MS;
    // 종료일을 **포함**하려면 그 다음 날 한국 자정이 배타적 끝이다.
    const endMs = Date.UTC(Number(e.y), Number(e.m) - 1, Number(e.d) + 1) - KST_OFFSET_MS;
    return {
        label: `${s.y}-${s.m}-${s.d}~${e.y}-${e.m}-${e.d}`,
        startUtc: new Date(startMs),
        endUtc: new Date(endMs),
    };
}
/**
 * KST 기준 `"YYYYMM"` — 월 단위 사람 읽는 식별자(`PP-YYYYMM-…`)용.
 * 값이 없거나 파싱 불가면 null(폴백은 호출부).
 */
export function kstYearMonth(v) {
    const p = kstParts(v);
    return p ? `${p.y}${p.m}` : null;
}
//# sourceMappingURL=kst.js.map